/**
 * GET /api/credit/abf/facilities/{id}/tape-surveillance (F-MK-075).
 *
 * A roll-rate cell is one (from state, to state) move between the two latest released tapes.
 * The current payload carries camelCase fields with the prior bucket's balance and the share of
 * it that moved; the older payload carried snake_case counts and the current balance only, so
 * both are typed and `normalizeRollRateCell` reads either.
 */

export const ROLL_STATES = ['CURRENT', 'LATE_30', 'LATE_60', 'LATE_90_PLUS', 'DEFAULTED', 'PAID_OFF', 'CHARGED_OFF', 'NEW'] as const;
export type RollState = typeof ROLL_STATES[number] | string;

export interface RollRateCell {
  fromState?: RollState;
  toState?: RollState;
  assetCount?: number | null;
  priorBalance?: number | null;
  currentBalance?: number | null;
  /** Share of the prior bucket's balance that moved to `toState`, in points (3.4 means 3.4%). */
  ratePct?: number | null;
  /** Older payload. */
  prior_bucket?: string;
  current_bucket?: string;
  asset_count?: number | null;
  balance?: number | null;
}

export interface NormalizedRollRateCell {
  fromState: string;
  toState: string;
  assetCount: number;
  priorBalance: number | null;
  currentBalance: number | null;
  ratePct: number | null;
}

export interface TapeSurveillance {
  latestTape?: Record<string, unknown>;
  latestTapeId?: number | null;
  tapeCount?: number;
  triggers?: Record<string, unknown>[];
  rollRates?: RollRateCell[];
  stratifications?: Record<string, unknown>[];
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const normalizeRollRateCell = (cell: RollRateCell): NormalizedRollRateCell => ({
  fromState: String(cell.fromState ?? cell.prior_bucket ?? 'UNKNOWN').toUpperCase(),
  toState: String(cell.toState ?? cell.current_bucket ?? 'UNKNOWN').toUpperCase(),
  assetCount: num(cell.assetCount ?? cell.asset_count) ?? 0,
  priorBalance: num(cell.priorBalance),
  currentBalance: num(cell.currentBalance ?? cell.balance),
  ratePct: num(cell.ratePct),
});

/** Canonical order first, anything the server adds after it, each once. */
export const orderRollStates = (states: Iterable<string>): string[] => {
  const seen = new Set(states);
  const ordered = ROLL_STATES.filter(state => seen.has(state)) as string[];
  const extra = Array.from(seen).filter(state => !(ROLL_STATES as readonly string[]).includes(state)).sort();
  return [...ordered, ...extra];
};
